import trace from '~/lib/trace';
import {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useCallback,
  useMemo,
  memo,
} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider, TextInput} from 'react-native-paper';
import {HeaderBackButton} from '@react-navigation/elements';
import moment from 'moment';
import isFinite from 'lodash/isFinite';

import sy from '~/styles';
import withProtectedScreen from '~/components/hoc/with-protected-screen';
import {
  Text,
  PrimaryButton,
  FormTextInput,
  FormNumberInput,
  Pressable,
  Dialog,
  RadioButton,
  Link,
  ReplacementIcon,
  ReplacementText,
  Checkbox,
  Calendar,
  MONTHS_3_PAST_6_FUTURE,
  MONTHS_6_FUTURE,
  TimeSlots,
  ServiceRequestStatusIcon,
  DealerPersona,
  LessorPersona,
  PrivateText,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {
  Row as GroupingRow,
  EditDialog as GroupingRowEdit,
} from '~/components/controls/3/Grouping';
import {
  KvkSearchDialog,
  RobNetCredentialsDialog,
} from '~/components/shared/Dialogs';
import {
  fleet as fleetApi,
  rob as robApi,
  car as carApi,
  service_request as serviceRequestApi,
} from '~/api/private';
import Format from '~/lib/format';
import UserContext from '~/components/context/UserContext';
import {SERVICE_TYPE_CODES} from '~/types/services';
import {STATUS_CODES} from '~/types/statuses';
import {REPLACEMENT_TYPE} from '~/types/replacement';
import {
  showTecrmiDialog,
  setAppIsLoading,
  setAppDoneLoading,
  showReservedClientMessageDialog,
} from '~/actions';
import {useDispatch, useSelector} from '~/lib/hooks';

import CloseIcon from '~/images/md-icons/close/materialicons/24px.svg';
import PriorityHighIcon from '~/images/md-icons/priority_high/materialicons/24px.svg';
import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import FlagIcon from '~/images/md-icons/flag/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import CallIcon from '~/images/md-icons/call/materialicons/24px.svg';
import AlternateEmailIcon from '~/images/md-icons/alternate_email/materialicons/24px.svg';
import SpeedIcon from '~/images/md-icons/speed/materialicons/24px.svg';
import CarRepairIcon from '~/images/md-icons/car_repair/materialicons/24px.svg';
import ApprovalIcon from '~/images/md-icons/approval/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import TransportationIcon from '~/images/md-icons/transportation/materialsymbolsfilled/24px.svg';
import InfoIcon from '~/images/md-icons/info/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import AccountCircleIcon from '~/images/md-icons/account_circle/materialicons/24px.svg';
import IdCardIcon from '~/images/md-icons/id_card/materialicons/24px.svg';
import ErrorOutlineIcon from '~/images/md-icons/error_outline/materialicons/24px.svg';
import WarningIcon from '~/images/md-icons/warning/materialicons/24px.svg';
import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';
import PinIcon from '~/images/md-icons/pin/materialicons/24px.svg';
import BuildIcon from '~/images/md-icons/build/materialsymbolsoutlined/24px.svg';
import CarCrashIcon from '~/images/md-icons/car_crash/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import RefreshIcon from '~/images/md-icons/refresh/materialicons/24px.svg';

import {emailRegex, phoneRegex} from './constants';
import ChangeDealerActionsheet from './components/ChangeDealerActionsheet';
import DuplicateWarningDialog from './components/DuplicateWarningDialog';
import ServiceTypeSubtitle from './components/ServiceTypeSubtitle';
import VehicleSearch from './components/VehicleSearch';
import PropTypes from 'prop-types';

const resolveLessorName = ({fleetContract, contract, isBusiness}) => {
  if (fleetContract) {
    return fleetContract.company_name;
  }

  if (contract) {
    return contract.lessor.name;
  }

  return isBusiness ? 'Zakelijk' : 'Particulier';
};

const maybeShowReservedClientMessage = ({
  dispatch,
  service_request_id,
  email,
  phone_number,
  status,
}) => {
  if (
    service_request_id &&
    (email || phone_number) &&
    status === STATUS_CODES.Reserved
  ) {
    dispatch(showReservedClientMessageDialog(service_request_id));
  }
};

const navigateToCreatedRequest = ({
  navigation,
  service_request_id,
  damage_request_id,
  replacement_request_id,
}) => {
  if (service_request_id) {
    navigation.replace('Request', {request_id: service_request_id});
    return;
  }

  if (damage_request_id) {
    navigation.replace('Damage', {
      damage_id: damage_request_id,
    });
    return;
  }

  if (replacement_request_id) {
    navigation.replace('ReplacementVehicle', {
      request_id: replacement_request_id,
    });
  }
};

const getPreferredDealer = (me) => {
  const dealers = me.dealers.sort((a, b) => a.city.localeCompare(b.city));

  let dealer = dealers.find((item) => item.is_default);
  if (!dealer) {
    dealer = dealers.find((item) => me?.dealer_selection?.includes(item.id));
  }

  if (!dealer) {
    dealer = dealers[0];
  }

  return dealer;
};

const getRouteParamEmailState = (routeParams, prevState) => {
  const value = routeParams?.email ?? prevState;

  return {
    value,
    hasError: !!(value && !value.match(emailRegex)),
  };
};

const getRouteParamPhoneState = (routeParams, prevState) => {
  const value = routeParams?.phonenumber?.slice(0, 10) ?? prevState;

  return {
    value,
    hasError: !!(value && !value.match(phoneRegex)),
  };
};

const shouldEnableRobServiceType = ({hasOpenStatus, isRetail, contract}) =>
  !hasOpenStatus &&
  (isRetail ||
    (contract?.isActive && contract?.permissions.repairAndMaintenance));

const shouldEnableInspectionByContract = ({
  contract,
  license,
  datetime,
  hasOpenStatus,
}) => {
  if (
    !contract?.isActive ||
    license !== contract?.vehicle?.licensePlate ||
    !contract?.permissions?.repairAndMaintenance ||
    !contract.vehicle?.maintenance?.nextPeriodicCheck ||
    hasOpenStatus
  ) {
    return false;
  }

  return moment(datetime)
    .add(2, 'months')
    .isAfter(moment(contract?.vehicle?.maintenance?.nextPeriodicCheck));
};

const performLicenseCheck = async ({
  license,
  dealer,
  overrideOpenStatus,
  setHasOpenStatus,
  setFleetContract,
  setContract,
  setCar,
}) => {
  const {result: status} = await serviceRequestApi.status_by_license(
    license,
    dealer.id,
  );
  const hasOpenStatus =
    status &&
    !overrideOpenStatus &&
    ![STATUS_CODES.Archived, STATUS_CODES.Cancelled].includes(status);
  setHasOpenStatus(hasOpenStatus);

  const {result: fleet_contract} = await fleetApi.contract_by_license(
    license,
    dealer.id,
  );

  let contract;
  if (fleet_contract?.isActive) {
    setFleetContract(fleet_contract);
    contract = fleet_contract.contract;
  } else {
    ({result: contract} = await robApi.contract_by_dealer(license, dealer.id));
  }

  setContract(contract);

  const car = await carApi.fetch(license);
  if (
    !car?.name &&
    contract?.vehicle?.vehicleMake &&
    contract?.vehicle?.vehicleModel
  ) {
    car.contract_name =
      `${contract.vehicle.vehicleMake} ${contract.vehicle.vehicleModel}`.toUpperCase();
  }

  setCar(car);

  return {
    check: contract || car?.name,
    contract,
    hasOpenStatus,
  };
};

const useAppointmentUiState = () => {
  const [initialized, setInitialized] = useState(false);
  const [license, setLicense] = useState('');
  const [car, setCar] = useState({});
  const [fleetContract, setFleetContract] = useState(null);
  const [contract, setContract] = useState(null);
  const [mileage, setMileage] = useState(null);
  const [initials, setInitials] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [serviceTypes, setServiceTypes] = useState([]);
  const [kvkNumber, setKvkNumber] = useState('');
  const [kvkName, setKvkName] = useState('');

  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [changeDealer, setChangeDealer] = useState(false);
  const [dealer, setDealer] = useState(null);
  const [datetime, setDatetime] = useState(
    moment
      .utc(moment().format('YYYY-MM-DDTHH:mm:ss'))
      .add(-1 * (moment().minute() % 30), 'minutes'),
  );
  const [editDateTime, setEditDateTime] = useState(null);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(null);
  const [vinPart, setVINPart] = useState(null);
  const [vinPartIncorrect, setVINPartIncorrect] = useState(null);
  const [vin, setVIN] = useState(null);
  const [replacementType, setReplacementType] = useState(REPLACEMENT_TYPE.none);
  const [status, setStatus] = useState(STATUS_CODES.Reserved);
  const [hasOpenStatus, setHasOpenStatus] = useState(false);
  const [overrideOpenStatus, setOverrideOpenStatus] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showInspectionInfoDialog, setShowInspectionInfoDialog] =
    useState(false);
  const [showKilometrageWarning, setShowKilometrageWarning] = useState(false);
  const [showVinPartRequiredDialog, setShowVinPartRequiredDialog] =
    useState(false);
  const [showRobNetCredentialsDialog, setShowRobNetCredentialsDialog] =
    useState(false);
  const [hasRetail, setHasRetail] = useState(false);
  const [isBusiness, setIsBusiness] = useState(null);
  const [showKvkDialog, setShowKvkDialog] = useState(false);
  const [enableTecRMI, setEnableTecRMI] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [showVehicleSearch, setShowVehicleSearch] = useState(false);
  const [robNetError, setRobNetError] = useState(null);

  return {
    initialized,
    setInitialized,
    license,
    setLicense,
    car,
    setCar,
    fleetContract,
    setFleetContract,
    contract,
    setContract,
    mileage,
    setMileage,
    initials,
    setInitials,
    first_name: firstName,
    setFirstName,
    last_name: lastName,
    setLastName,
    email,
    setEmail,
    phone_number: phoneNumber,
    setPhoneNumber,
    serviceTypes,
    setServiceTypes,
    kvk_number: kvkNumber,
    setKvkNumber,
    kvk_name: kvkName,
    setKvkName,
    emailError,
    setEmailError,
    phoneError,
    setPhoneError,
    changeDealer,
    setChangeDealer,
    dealer,
    setDealer,
    datetime,
    setDatetime,
    editDateTime,
    setEditDateTime,
    expected_completion_date: expectedCompletionDate,
    setExpectedCompletionDate,
    vin_part: vinPart,
    setVINPart,
    vin_part_incorrect: vinPartIncorrect,
    setVINPartIncorrect,
    vin,
    setVIN,
    replacementType,
    setReplacementType,
    status,
    setStatus,
    hasOpenStatus,
    setHasOpenStatus,
    overrideOpenStatus,
    setOverrideOpenStatus,
    showDuplicateDialog,
    setShowDuplicateDialog,
    showInspectionInfoDialog,
    setShowInspectionInfoDialog,
    showKilometrageWarning,
    setShowKilometrageWarning,
    showVinPartRequiredDialog,
    setShowVinPartRequiredDialog,
    showRobNetCredentialsDialog,
    setShowRobNetCredentialsDialog,
    hasRetail,
    setHasRetail,
    isBusiness,
    setIsBusiness,
    showKvkDialog,
    setShowKvkDialog,
    enableTecRMI,
    setEnableTecRMI,
    isLicenseValid,
    setIsLicenseValid,
    showVehicleSearch,
    setShowVehicleSearch,
    robNetError,
    setRobNetError,
  };
};

const useAppointmentHeader = (navigation) => {
  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: () => (
        <HeaderBackButton
          backImage={() => <CloseIcon />}
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
              return;
            }

            navigation.replace('Tabs', {screen: 'Calendar'});
          }}
        />
      ),
      headerStatusBarHeight: 0,
      headerStyle: {
        borderBottomWidth: 0,
        boxShadow: 'none',
        height: 56,
      },
      headerTitleAlign: 'center',
      headerTitle: () => (
        <Text style={[sy.mediumPlus, {fontSize: 14}]}>Concept</Text>
      ),
      headerRight: () => (
        <View style={[sy['pr-4']]}>
          <ServiceRequestStatusIcon status={STATUS_CODES.Draft} />
        </View>
      ),
    });
  }, [navigation]);
};

const getAppointmentPermissionLabel = (allowed) =>
  allowed ? <>Toegestaan</> : <>Niet toegestaan</>;

const getAppointmentLessorFallbackColor = ({
  contract_ends,
  contract,
  car,
  hasRetail,
  isBusiness,
}) => {
  if (contract_ends || contract?.isActive === false) {
    return '#972727';
  }

  if (car?.name && !contract && hasRetail && isBusiness === null) {
    return '#FF6C00';
  }

  return '#4A4A49';
};

const getAppointmentLessorHeader = ({fleetContract, contract}) => {
  if (fleetContract) {
    return <>{fleetContract.company_name}</>;
  }

  if (contract) {
    return contract?.lessor?.name ?? 'Onbekend';
  }

  return 'Voer kenteken in';
};

const getAppointmentDamagePermissionLabel = ({fleetContract, contract}) => {
  const onlyDamageByPermission =
    !fleetContract &&
    contract?.supplierContract?.repairAndMaintenance
      .componentsWithDamageAllowed === false;

  if (onlyDamageByPermission) {
    return contract?.permissions?.damage
      ? 'Toegestaan (opdracht)'
      : 'Niet toegestaan';
  }

  return 'Toegestaan (kenmerk)';
};

const AppointmentLessorGrouping = memo(
  ({
    isRetail,
    fleetContract,
    contract,
    contract_ends,
    car,
    hasRetail,
    isBusiness,
    license,
    dealer,
    setContract,
  }) => {
    if (isRetail) {
      return null;
    }

    const fallbackColor = getAppointmentLessorFallbackColor({
      contract_ends,
      contract,
      car,
      hasRetail,
      isBusiness,
    });

    const lessorHeader = getAppointmentLessorHeader({fleetContract, contract});

    const damagePermissionLabel = getAppointmentDamagePermissionLabel({
      fleetContract,
      contract,
    });

    return (
      <Grouping
        icon={
          <LessorPersona
            key={fleetContract?.company_name ?? contract?.lessor?.name}
            lessor={fleetContract?.company_name ?? contract?.lessor?.name}
            fallback={<BusinessIcon fill={fallbackColor} />}
            size={24}
          />
        }
        header={lessorHeader}>
        {contract ? (
          <>
            {contract.leaseContract?.endDate && (
              <GroupingRow
                title={
                  <View style={[sy['flex-row'], sy['items-center']]}>
                    <Text>Einde contract</Text>
                    {contract_ends && (
                      <>
                        &nbsp;
                        <WarningIcon fill="#972727" />
                      </>
                    )}
                  </View>
                }>
                <Text>
                  {moment(contract?.leaseContract.endDate).format('D-M-YYYY')}
                  &nbsp;(
                  {Format.number(
                    contract?.leaseContract.maximumMileage,
                    0,
                    true,
                  )}{' '}
                  km)
                </Text>
              </GroupingRow>
            )}
            <GroupingRow title="Reparatie & onderhoud">
              <Text>
                {getAppointmentPermissionLabel(
                  contract?.permissions?.repairAndMaintenance,
                )}
              </Text>
            </GroupingRow>
            <GroupingRow title="Schade">
              <Text>{damagePermissionLabel}</Text>
            </GroupingRow>
            <GroupingRow title="Bandvervanging">
              <Text>
                {getAppointmentPermissionLabel(
                  contract?.permissions?.tires.replace,
                )}
              </Text>
            </GroupingRow>
            <GroupingRow title="Seizoenswissel">
              <Text>
                {getAppointmentPermissionLabel(
                  contract?.permissions?.tires.swap,
                )}
              </Text>
            </GroupingRow>
            <GroupingRow title="Vervangend vervoer">
              <Text>
                {getAppointmentPermissionLabel(
                  contract?.permissions?.replacement_vehicle?.allowed,
                )}
              </Text>
            </GroupingRow>
            {!fleetContract && (
              <GroupingRow>
                <View style={[sy['flex-row'], sy['justify-between']]}>
                  <Pressable
                    style={[sy['flex-row']]}
                    onPress={async () => {
                      const {result: rob_contract} =
                        await robApi.update_contract(license, null, dealer.id);

                      setContract(rob_contract);
                    }}>
                    <View style={[sy['pr-8']]}>
                      <RefreshIcon fill="#231fda" />
                    </View>
                    <View>
                      <Link>Gegevens vernieuwen</Link>
                      <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                        Laatst bijgewerkt&nbsp;
                        {moment(contract?.timestamp).format('D MMMM YYYY H:mm')}
                      </Text>
                    </View>
                  </Pressable>
                </View>
              </GroupingRow>
            )}
          </>
        ) : null}
      </Grouping>
    );
  },
);

AppointmentLessorGrouping.displayName = 'AppointmentLessorGrouping';

const AppointmentPersonGrouping = memo(
  ({
    initials,
    first_name,
    last_name,
    isValid,
    emailError,
    phoneError,
    isRetail,
    setInitials,
    setFirstName,
    setLastName,
    phone_number,
    setPhoneNumber,
    setPhoneError,
    email,
    setEmail,
    setEmailError,
  }) => {
    const phoneMissing = !phone_number?.length;
    const phoneInvalid =
      !!phone_number?.length && !phone_number?.match(phoneRegex);
    let phoneErrorMessage = null;

    if (phoneMissing) {
      phoneErrorMessage = 'Mobiele telefoonnummer is verplicht';
    } else if (phoneInvalid) {
      phoneErrorMessage = 'Ongeldige waarde mobiele telefoonnummer';
    }

    return (
      <Grouping
        icon={<IdCardIcon fill="#4A4A49" />}
        header={
          <View style={[sy['flex-row']]}>
            <PrivateText style={[sy.mediumPlus]}>
              {initials || first_name || last_name
                ? `${first_name || initials || ''} ${last_name || ''}`.trim()
                : 'Berijder'}
            </PrivateText>
            {!isValid && (emailError || phoneError) && (
              <>
                <Text style={sy.mediumPlus}>&nbsp;&middot;&nbsp;</Text>
                <Text style={[sy.mediumPlus, {color: '#FF6C00'}]}>
                  Gegevens invoeren
                </Text>
              </>
            )}
          </View>
        }
        initial={{
          expand: isRetail || emailError || phoneError,
        }}>
        <View style={[sy['p-4'], sy['gap-4']]}>
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <View style={[sy['py-4']]}>
              <AccountCircleIcon fill="#4a4a49" />
            </View>
            <View style={[sy['flex-1']]}>
              <div data-private>
                <FormTextInput
                  label="Voorletter(s)"
                  value={initials}
                  onChangeText={setInitials}
                />
              </div>
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <View style={[sy['py-4']]}>
              <View style={{width: 24, height: 24}}>
                <Text>&nbsp;</Text>
              </View>
            </View>
            <View style={[sy['flex-1']]}>
              <div data-private>
                <FormTextInput
                  label="Voornaam"
                  value={first_name}
                  onChangeText={setFirstName}
                />
              </div>
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <View style={[sy['py-4']]}>
              <View style={{width: 24, height: 24}}>
                <Text>&nbsp;</Text>
              </View>
            </View>
            <View style={[sy['flex-1']]}>
              <div data-private>
                <FormTextInput
                  label="Achternaam"
                  value={last_name}
                  onChangeText={setLastName}
                />
              </div>
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <View style={[sy['py-4']]}>
              <CallIcon fill="#4a4a49" />
            </View>
            <View style={[sy['flex-1']]}>
              <div data-private>
                <FormTextInput
                  value={phone_number}
                  onChangeText={(value) => {
                    setPhoneNumber(() => {
                      setPhoneError(false);
                      return value;
                    });
                  }}
                  onEndEditing={() => {
                    const valid =
                      !phone_number || !!phone_number.match(phoneRegex);
                    setPhoneError(!valid);
                  }}
                  label="Mobiele telefoonnummer"
                  hasError={phoneError}
                  errorMessage={phoneErrorMessage}
                  digits={0}
                />
              </div>
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-4']]}>
            <View style={[sy['py-4']]}>
              <AlternateEmailIcon fill="#4a4a49" />
            </View>
            <View style={[sy['flex-1']]}>
              <div data-private>
                <FormTextInput
                  label="E-mail"
                  value={email}
                  onChangeText={(value) => {
                    setEmail(() => {
                      setEmailError(false);
                      return value;
                    });
                  }}
                  onEndEditing={() => {
                    const valid = !email || !!email.match(emailRegex);
                    setEmailError(!valid);
                  }}
                  hasError={emailError}
                  errorMessage="E-mail heeft een ongeldige waarde"
                />
              </div>
            </View>
          </View>
        </View>
      </Grouping>
    );
  },
);

AppointmentPersonGrouping.displayName = 'AppointmentPersonGrouping';

const renderAppointmentDetailsGate = ({
  hasRetail,
  car,
  contract,
  isLicenseValid,
  isRetail,
  isBusiness,
  kvk_number,
  children,
}) => {
  if (!hasRetail && car?.name && !contract?.isActive) {
    return (
      <Grouping
        icon={<BusinessIcon fill="#FF6C00" />}
        header="Lease/wagenpark niet gevonden"
      />
    );
  }

  if (
    !(isLicenseValid && (!isRetail || isBusiness === false || !!kvk_number))
  ) {
    return null;
  }

  return <>{children}</>;
};

const AppointmentExtraServiceGrouping = memo(
  ({
    serviceTypes,
    hasOpenStatus,
    contract,
    replacementType,
    setReplacementType,
  }) => {
    if (
      serviceTypes.includes(SERVICE_TYPE_CODES.Replacement) ||
      hasOpenStatus ||
      !contract?.permissions?.repairAndMaintenance
    ) {
      return null;
    }

    return (
      <Grouping
        icon={<TransportationIcon fill="#4A4A49" />}
        header="Extra service"
        initial={{expand: true}}>
        <View>
          {Object.values(REPLACEMENT_TYPE).map((type, index, arr) => {
            if (type === REPLACEMENT_TYPE.car) {
              return null;
            }

            return (
              <Pressable
                key={type}
                style={[sy['flex-row'], sy['flex-1'], sy['gap-4'], sy['px-4']]}
                onPress={() => {
                  if (replacementType === type) {
                    setReplacementType(null);
                    return;
                  }

                  setReplacementType(type);
                }}>
                <View style={[sy['py-4']]}>
                  <RadioButton checked={replacementType === type} />
                </View>
                <View style={[sy['flex-1']]}>
                  <View
                    style={[
                      sy['flex-row'],
                      sy['flex-1'],
                      sy['items-center'],
                      sy['justify-between'],
                      sy['py-4'],
                    ]}>
                    <View>
                      <ReplacementText type={type} />
                    </View>
                    <ReplacementIcon type={type} fill="#4A4A49" />
                  </View>
                  {index < arr.length - 1 && (
                    <Divider style={sy['bg-gray95']} />
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>
      </Grouping>
    );
  },
);

AppointmentExtraServiceGrouping.displayName = 'AppointmentExtraServiceGrouping';

const toggleAppointmentRobMainType = ({setServiceTypes}) => {
  const type = SERVICE_TYPE_CODES.ROB;

  setServiceTypes((prevState) =>
    prevState.includes(type)
      ? prevState.filter(
          (item) =>
            ![
              type,
              SERVICE_TYPE_CODES.Maintenance,
              SERVICE_TYPE_CODES.Repairs,
              SERVICE_TYPE_CODES.Inspection,
              SERVICE_TYPE_CODES.Tires,
              SERVICE_TYPE_CODES.ComponentsWithDamage,
            ].includes(item),
        )
      : [...new Set([...prevState, type])],
  );
};

const toggleAppointmentRobRelatedType = ({setServiceTypes, type}) => {
  setServiceTypes((prevState) =>
    prevState.includes(type)
      ? prevState.filter((item) => item !== type)
      : [...new Set([...prevState, type, SERVICE_TYPE_CODES.ROB])],
  );
};

const toggleAppointmentStandaloneType = ({setServiceTypes, type}) => {
  setServiceTypes((prevState) =>
    prevState.includes(type)
      ? prevState.filter((item) => item !== type)
      : [...new Set([...prevState, type])],
  );
};

const handleAppointmentMaintenanceToggle = async ({
  isRetail,
  isBusiness,
  contract,
  serviceTypes,
  setServiceTypes,
  dispatch,
  setEnableTecRMI,
}) => {
  const type = SERVICE_TYPE_CODES.Maintenance;
  const tecrmi_exemption = isRetail
    ? isBusiness !== null
    : contract?.permissions.tecrmi;

  if (!serviceTypes.includes(type)) {
    if (tecrmi_exemption) {
      // Do nothing. TecRMI will be enabled when the service request is created.
    } else {
      try {
        await new Promise((resolve, reject) => {
          dispatch(
            showTecrmiDialog({
              resolve,
              reject,
            }),
          );
        });

        setEnableTecRMI(true);
      } catch {
        return;
      }
    }

    if (
      !serviceTypes.includes(SERVICE_TYPE_CODES.Inspection) &&
      contract?.vehicle?.maintenance?.nextPeriodicCheckExtended &&
      moment().isAfter(
        moment(contract?.vehicle?.maintenance?.nextPeriodicCheckExtended),
        'date',
      )
    ) {
      serviceTypes.push(SERVICE_TYPE_CODES.Inspection);
    }
  }

  toggleAppointmentRobRelatedType({setServiceTypes, type});
};

const renderAppointmentInspectionStatusIcon = ({
  contract,
  datetime,
  setShowInspectionInfoDialog,
}) => {
  if (
    contract?.vehicle?.maintenance?.nextPeriodicCheck &&
    moment(datetime)
      .add(2, 'months')
      .isAfter(moment(contract?.vehicle?.maintenance?.nextPeriodicCheck))
  ) {
    return <PriorityHighIcon fill="#972727" width={18} height={18} />;
  }

  if (
    contract?.vehicle?.maintenance?.nextPeriodicCheckExtended &&
    moment().isAfter(
      moment(contract?.vehicle?.maintenance?.nextPeriodicCheckExtended),
      'date',
    )
  ) {
    return (
      <Pressable onPress={() => setShowInspectionInfoDialog(true)}>
        <InfoIcon fill="#FF6C00" width={18} height={18} />
      </Pressable>
    );
  }

  return null;
};

const renderAppointmentRobMainRow = ({
  isRetail,
  contract,
  hasOpenStatus,
  setShowDuplicateDialog,
  serviceTypes,
  setServiceTypes,
}) => (
  <Pressable
    style={[
      sy['flex-row'],
      sy['px-4'],
      sy['items-center'],
      !isRetail &&
        !contract?.permissions?.repairAndMaintenance &&
        sy['view-disabled'],
    ]}
    disabled={
      (!isRetail && !contract?.permissions?.repairAndMaintenance) ||
      hasOpenStatus
    }
    onPress={() => {
      toggleAppointmentRobMainType({setServiceTypes});
    }}>
    {hasOpenStatus ? (
      <Pressable onPress={() => setShowDuplicateDialog(true)}>
        <ErrorIcon fill="#B3261E" />
      </Pressable>
    ) : (
      <Checkbox
        disabled={
          (!isRetail && !contract?.permissions?.repairAndMaintenance) ||
          hasOpenStatus
        }
        checked={serviceTypes.includes(SERVICE_TYPE_CODES.ROB)}
      />
    )}
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['justify-between'],
          sy['pl-4'],
          sy['py-4'],
          sy['items-center'],
        ]}>
        <View style={[sy['flex-1']]}>
          <Text>ROB</Text>
          <Text style={[sy.small, sy['text-lightgray']]}>
            {isRetail || contract?.permissions?.repairAndMaintenance ? (
              <>Opent werkopdracht</>
            ) : (
              <>Niet toegestaan in contract</>
            )}
          </Text>
        </View>
        <BuildIcon fill="#4A4A49" />
      </View>
    </View>
  </Pressable>
);

const renderAppointmentRobSubTypeRows = ({
  isRetail,
  contract,
  hasOpenStatus,
  isBusiness,
  dispatch,
  serviceTypes,
  setServiceTypes,
  setEnableTecRMI,
  car,
  mileage,
  datetime,
  setShowInspectionInfoDialog,
  fleetContract,
}) => {
  if (
    !(isRetail || contract?.permissions?.repairAndMaintenance) ||
    hasOpenStatus
  ) {
    return <Divider style={[sy['mx-4'], sy['bg-gray95']]} />;
  }

  return (
    <>
      <Pressable
        style={[sy['flex-row'], sy['px-4'], sy['ml-8'], sy['items-center']]}
        onPress={async () => {
          await handleAppointmentMaintenanceToggle({
            isRetail,
            isBusiness,
            contract,
            serviceTypes,
            setServiceTypes,
            dispatch,
            setEnableTecRMI,
          });
        }}>
        <View style={[sy['flex-1']]}>
          <Divider style={sy['bg-gray95']} />
          <View style={[sy['flex-row'], sy['justify-between'], sy['py-4']]}>
            <View
              style={[
                sy['flex-row'],
                sy['gap-4'],
                sy['flex-1'],
                sy['items-center'],
              ]}>
              <Checkbox
                checked={serviceTypes.includes(SERVICE_TYPE_CODES.Maintenance)}
              />
              <View style={[sy['flex-1']]}>
                <Text>Onderhoud</Text>
                <Text style={[sy.small, sy['text-lightgray']]}>
                  <ServiceTypeSubtitle
                    type={SERVICE_TYPE_CODES.Maintenance}
                    contract={contract}
                    car={
                      car && mileage
                        ? {
                            kilometrage: mileage,
                            ascription: car.entry_date,
                          }
                        : null
                    }
                  />
                </Text>
              </View>
              <CarRepairIcon fill="#4a4a49" />
            </View>
          </View>
          <Divider style={sy['bg-gray95']} />
        </View>
      </Pressable>
      <Pressable
        style={[sy['flex-row'], sy['px-4'], sy['ml-8'], sy['items-center']]}
        onPress={async () => {
          toggleAppointmentRobRelatedType({
            setServiceTypes,
            type: SERVICE_TYPE_CODES.Inspection,
          });
        }}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['justify-between'], sy['py-4']]}>
            <View
              style={[
                sy['flex-row'],
                sy['gap-4'],
                sy['flex-1'],
                sy['items-center'],
              ]}>
              <Checkbox
                checked={serviceTypes.includes(SERVICE_TYPE_CODES.Inspection)}
              />
              <View style={[sy['flex-1']]}>
                <Text>APK</Text>
                <View style={[sy['flex-row'], sy['gap-2']]}>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    <ServiceTypeSubtitle
                      type={SERVICE_TYPE_CODES.Inspection}
                      contract={contract}
                      car={
                        car && mileage
                          ? {
                              kilometrage: mileage,
                              ascription: car.entry_date,
                            }
                          : null
                      }
                    />
                  </Text>
                  {renderAppointmentInspectionStatusIcon({
                    contract,
                    datetime,
                    setShowInspectionInfoDialog,
                  })}
                </View>
              </View>
              <ApprovalIcon fill="#4a4a49" />
            </View>
          </View>
          <Divider style={sy['bg-gray95']} />
        </View>
      </Pressable>
      <Pressable
        style={[sy['flex-row'], sy['px-4'], sy['ml-8'], sy['items-center']]}
        onPress={async () => {
          toggleAppointmentRobRelatedType({
            setServiceTypes,
            type: SERVICE_TYPE_CODES.Tires,
          });
        }}>
        <View style={[sy['flex-1']]}>
          <View style={[sy['flex-row'], sy['justify-between'], sy['py-4']]}>
            <View
              style={[
                sy['flex-row'],
                sy['gap-4'],
                sy['flex-1'],
                sy['items-center'],
              ]}>
              <Checkbox
                checked={serviceTypes.includes(SERVICE_TYPE_CODES.Tires)}
              />
              <View style={[sy['flex-1']]}>
                <Text>Banden</Text>
                <Text style={[sy.small, sy['text-lightgray']]}>
                  <ServiceTypeSubtitle
                    type={SERVICE_TYPE_CODES.Tires}
                    contract={contract}
                    car={
                      car && mileage
                        ? {
                            kilometrage: mileage,
                            ascription: car.entry_date,
                          }
                        : null
                    }
                  />
                </Text>
              </View>
              <TireRepairIcon fill="#4a4a49" />
            </View>
          </View>
          <Divider style={sy['bg-gray95']} />
        </View>
      </Pressable>
      {(fleetContract ||
        contract?.supplierContract?.repairAndMaintenance
          ?.componentsWithDamageAllowed) && (
        <Pressable
          style={[sy['flex-row'], sy['px-4'], sy['ml-8'], sy['items-center']]}
          onPress={async () => {
            toggleAppointmentRobRelatedType({
              setServiceTypes,
              type: SERVICE_TYPE_CODES.ComponentsWithDamage,
            });
          }}>
          <View style={[sy['flex-1']]}>
            <View style={[sy['flex-row'], sy['justify-between'], sy['py-4']]}>
              <View
                style={[
                  sy['flex-row'],
                  sy['gap-4'],
                  sy['flex-1'],
                  sy['items-center'],
                ]}>
                <Checkbox
                  checked={serviceTypes.includes(
                    SERVICE_TYPE_CODES.ComponentsWithDamage,
                  )}
                />
                <View style={[sy['flex-1']]}>
                  <Text>Schade</Text>
                  <Text style={[sy.small, sy['text-lightgray']]}>
                    Gebruik kenmerk in activiteit
                  </Text>
                </View>
                <CarCrashIcon fill="#4a4a49" />
              </View>
            </View>
            <Divider style={sy['bg-gray95']} />
          </View>
        </Pressable>
      )}
    </>
  );
};

const renderAppointmentDamageRequestRow = ({
  fleetContract,
  contract,
  setServiceTypes,
  serviceTypes,
}) => {
  if (
    fleetContract ||
    contract?.supplierContract?.repairAndMaintenance
      ?.componentsWithDamageAllowed !== false
  ) {
    return null;
  }

  return (
    <>
      <Pressable
        style={[
          sy['flex-row'],
          sy['px-4'],
          sy['items-center'],
          !contract?.permissions.damage && sy['view-disabled'],
        ]}
        disabled={!contract?.permissions.damage}
        onPress={() => {
          toggleAppointmentStandaloneType({
            setServiceTypes,
            type: SERVICE_TYPE_CODES.DamageRequest,
          });
        }}>
        <Checkbox
          disabled={!contract?.permissions?.damage}
          checked={serviceTypes.includes(SERVICE_TYPE_CODES.DamageRequest)}
        />
        <View style={[sy['flex-1']]}>
          <View
            style={[
              sy['flex-row'],
              sy['items-center'],
              sy['justify-between'],
              sy['pl-4'],
              sy['py-4'],
            ]}>
            <View>
              <Text>Schade</Text>
              <Text style={[sy.small, sy['text-lightgray']]}>
                {contract?.permissions?.damage ? (
                  <>Opent schadewerkopdracht</>
                ) : (
                  <>Niet toegestaan in contract</>
                )}
              </Text>
            </View>
            <CarCrashIcon fill="#4A4A49" />
          </View>
        </View>
      </Pressable>
      <Divider style={[sy['mx-4'], sy['bg-gray95']]} />
    </>
  );
};

const renderAppointmentReplacementRequestRow = ({
  contract,
  setServiceTypes,
  serviceTypes,
}) => (
  <Pressable
    style={[
      sy['flex-row'],
      sy['px-4'],
      sy['items-center'],
      !contract?.permissions?.replacement_vehicle?.allowed &&
        sy['view-disabled'],
    ]}
    disabled={!contract?.permissions?.replacement_vehicle?.allowed}
    onPress={() => {
      toggleAppointmentStandaloneType({
        setServiceTypes,
        type: SERVICE_TYPE_CODES.Replacement,
      });
    }}>
    <Checkbox
      disabled={!contract?.permissions?.replacement_vehicle?.allowed}
      checked={serviceTypes.includes(SERVICE_TYPE_CODES.Replacement)}
    />
    <View style={[sy['flex-1']]}>
      <View
        style={[
          sy['flex-row'],
          sy['justify-between'],
          sy['items-center'],
          sy['pl-4'],
          sy['py-4'],
        ]}>
        <View>
          <Text>Huurauto</Text>
          <Text style={[sy.small, sy['text-lightgray']]}>
            {contract?.permissions?.replacement_vehicle?.allowed ? (
              <>Opent vervangend vervoer opdracht</>
            ) : (
              <>Niet toegestaan in contract</>
            )}
          </Text>
        </View>
        <CarRentalIcon fill="#4A4A49" />
      </View>
      <Divider style={sy['bg-gray95']} />
    </View>
  </Pressable>
);

const AppointmentAssignmentGrouping = memo(
  ({
    isRetail,
    contract,
    hasOpenStatus,
    setShowDuplicateDialog,
    serviceTypes,
    setServiceTypes,
    isBusiness,
    dispatch,
    setEnableTecRMI,
    car,
    mileage,
    datetime,
    setShowInspectionInfoDialog,
    fleetContract,
  }) => (
    <Grouping
      icon={<AssignmentIcon fill="#4A4A49" />}
      header="Opdracht"
      initial={{expand: true}}>
      <View>
        {renderAppointmentRobMainRow({
          isRetail,
          contract,
          hasOpenStatus,
          setShowDuplicateDialog,
          serviceTypes,
          setServiceTypes,
        })}
        {renderAppointmentRobSubTypeRows({
          isRetail,
          contract,
          hasOpenStatus,
          isBusiness,
          dispatch,
          serviceTypes,
          setServiceTypes,
          setEnableTecRMI,
          car,
          mileage,
          datetime,
          setShowInspectionInfoDialog,
          fleetContract,
        })}
        {renderAppointmentDamageRequestRow({
          fleetContract,
          contract,
          setServiceTypes,
          serviceTypes,
        })}
        {renderAppointmentReplacementRequestRow({
          contract,
          setServiceTypes,
          serviceTypes,
        })}
      </View>
    </Grouping>
  ),
);

AppointmentAssignmentGrouping.displayName = 'AppointmentAssignmentGrouping';

const runAppointmentLicenseCheck = async ({
  license,
  dealer,
  overrideOpenStatus,
  reset,
  setHasOpenStatus,
  setFleetContract,
  setContract,
  setCar,
}) => {
  if (!license || license.length < 6) {
    reset();
    return {
      check: false,
    };
  }

  try {
    return await performLicenseCheck({
      license,
      dealer,
      overrideOpenStatus,
      setHasOpenStatus,
      setFleetContract,
      setContract,
      setCar,
    });
  } catch {
    reset();
  }

  return {
    check: false,
  };
};

const submitAppointmentRequest = async ({
  isValid,
  licenseCheck,
  dispatch,
  status,
  license,
  mileage,
  initials,
  first_name,
  last_name,
  email,
  phone_number,
  dealer,
  datetime,
  fleetContract,
  contract,
  isBusiness,
  serviceTypes,
  vin_part,
  vin,
  replacementType,
  kvk_number,
  kvk_name,
  enableTecRMI,
  expected_completion_date,
  navigation,
}) => {
  if (!isValid) {
    return;
  }

  const {check} = await licenseCheck();
  if (!check) {
    return;
  }

  dispatch(setAppIsLoading());

  try {
    const {
      success,
      service_request_id,
      damage_request_id,
      replacement_request_id,
    } = await serviceRequestApi.post({
      status,
      license,
      mileage,
      initials,
      first_name,
      last_name,
      email,
      phone_number,
      dealer_id: dealer.id,
      datetime,
      lessor: resolveLessorName({
        fleetContract,
        contract,
        isBusiness,
      }),
      types: serviceTypes,
      vin_part,
      vin_number: vin,
      replacement_type:
        replacementType === REPLACEMENT_TYPE.none ? null : replacementType,
      is_fleet_car: !!fleetContract,
      is_business: isBusiness,
      kvk_number: isBusiness === true ? kvk_number : null,
      kvk_name: isBusiness === true ? kvk_name : null,
      enable_tecrmi: enableTecRMI,
      expected_completion_date: expected_completion_date?.format('YYYY-MM-DD'),
    });

    if (success) {
      if (!fleetContract) {
        maybeShowReservedClientMessage({
          dispatch,
          service_request_id,
          email,
          phone_number,
          status,
        });
      }

      navigateToCreatedRequest({
        navigation,
        service_request_id,
        damage_request_id,
        replacement_request_id,
      });
    }
  } finally {
    dispatch(setAppDoneLoading());
  }
};

const syncAppointmentDealerFromProfile = ({
  me,
  dealer,
  setDealer,
  setHasRetail,
}) => {
  if (!me || dealer) {
    return;
  }

  const preferredDealer = getPreferredDealer(me);
  setDealer(preferredDealer);
  setHasRetail(preferredDealer?.has_retail);
};

const applyAppointmentVinLookupState = ({
  vin_number,
  vin_part,
  setVIN,
  setVINPartIncorrect,
}) => {
  if (vin_number?.endsWith(vin_part)) {
    setVIN(vin_number);
    setVINPartIncorrect(false);
    return;
  }

  setVIN(null);
  setVINPartIncorrect(true);
};

const runAppointmentVinLookupEffect = async ({
  initialized,
  license,
  vin_part,
  dealer,
  setRobNetError,
  setShowRobNetCredentialsDialog,
  setVIN,
  setVINPartIncorrect,
}) => {
  if (!initialized) {
    return;
  }

  if (!(license?.length > 5 && vin_part?.length === 4)) {
    setVIN(null);
    setVINPartIncorrect(null);
    return;
  }

  const {vin_number, need_rob_net_login} = await carApi.vinLookup(
    license,
    vin_part,
    dealer.id,
  );

  if (need_rob_net_login) {
    setRobNetError(null);
    setShowRobNetCredentialsDialog(true);
    return;
  }

  applyAppointmentVinLookupState({
    vin_number,
    vin_part,
    setVIN,
    setVINPartIncorrect,
  });
};

const resolveAppointmentStatusFromVin = ({datetime, vin}) => {
  if (
    !vin ||
    datetime.isAfter(moment.utc(moment().format('YYYY-MM-DD')), 'days')
  ) {
    return STATUS_CODES.Reserved;
  }

  return STATUS_CODES.CheckIn;
};

const computeAppointmentIsValid = ({
  serviceTypes,
  license,
  vin_part,
  car,
  vin_part_incorrect,
  contract,
  isRetail,
  isBusiness,
  kvk_number,
  kvk_name,
  email,
  phone_number,
  dealer,
  datetime,
}) => {
  const hasValidMainServiceType = [
    SERVICE_TYPE_CODES.ROB,
    SERVICE_TYPE_CODES.DamageRequest,
    SERVICE_TYPE_CODES.Replacement,
  ].some((item) => serviceTypes.includes(item));

  const hasValidVinPart =
    (!vin_part?.length && car.name) ||
    (vin_part?.length === 4 && vin_part_incorrect === false);

  const hasValidContractState =
    contract?.isActive ||
    (isRetail ? isBusiness === false || (!!kvk_number && !!kvk_name) : false);

  const hasValidEmail = !email || !!email.match(emailRegex);
  const hasValidPhone = !phone_number || !!phone_number?.match(phoneRegex);

  return (
    serviceTypes.length > 0 &&
    hasValidMainServiceType &&
    license.length > 5 &&
    hasValidVinPart &&
    hasValidContractState &&
    hasValidEmail &&
    hasValidPhone &&
    !!dealer &&
    !!datetime
  );
};

const Index = ({navigation, route}) => {
  const {
    initialized,
    setInitialized,
    license,
    setLicense,
    car,
    setCar,
    fleetContract,
    setFleetContract,
    contract,
    setContract,
    mileage,
    setMileage,
    initials,
    setInitials,
    first_name,
    setFirstName,
    last_name,
    setLastName,
    email,
    setEmail,
    phone_number,
    setPhoneNumber,
    serviceTypes,
    setServiceTypes,
    kvk_number,
    setKvkNumber,
    kvk_name,
    setKvkName,
    emailError,
    setEmailError,
    phoneError,
    setPhoneError,
    changeDealer,
    setChangeDealer,
    dealer,
    setDealer,
    datetime,
    setDatetime,
    editDateTime,
    setEditDateTime,
    expected_completion_date,
    setExpectedCompletionDate,
    vin_part,
    setVINPart,
    vin_part_incorrect,
    setVINPartIncorrect,
    vin,
    setVIN,
    replacementType,
    setReplacementType,
    status,
    setStatus,
    hasOpenStatus,
    setHasOpenStatus,
    overrideOpenStatus,
    setOverrideOpenStatus,
    showDuplicateDialog,
    setShowDuplicateDialog,
    showInspectionInfoDialog,
    setShowInspectionInfoDialog,
    showKilometrageWarning,
    setShowKilometrageWarning,
    showVinPartRequiredDialog,
    setShowVinPartRequiredDialog,
    showRobNetCredentialsDialog,
    setShowRobNetCredentialsDialog,
    hasRetail,
    setHasRetail,
    isBusiness,
    setIsBusiness,
    showKvkDialog,
    setShowKvkDialog,
    enableTecRMI,
    setEnableTecRMI,
    isLicenseValid,
    setIsLicenseValid,
    showVehicleSearch,
    setShowVehicleSearch,
    robNetError,
    setRobNetError,
  } = useAppointmentUiState();

  // Pinned once per mount: `moment()` inline would hand Calendar a new value on
  // every render and defeat its layout memo.
  const today = useMemo(() => moment(), []);

  const dispatch = useDispatch();
  const user_settings = useSelector((state) => state.user_settings);

  const isRetail = !fleetContract && !contract && hasRetail;

  const requestButtonLabel = (() => {
    if (
      serviceTypes?.length === 1 &&
      serviceTypes[0] === SERVICE_TYPE_CODES.Replacement
    ) {
      return 'Aanmaken';
    }

    if (status === STATUS_CODES.Reserved) {
      return 'Reserveren';
    }

    return 'Inchecken';
  })();

  useAppointmentHeader(navigation);

  const {me} = useContext(UserContext);

  useEffect(() => {
    syncAppointmentDealerFromProfile({
      me,
      dealer,
      setDealer,
      setHasRetail,
    });
  }, [me, dealer, setDealer, setHasRetail]);

  useEffect(() => {
    setInitialized(false);

    setTimeout(() => {
      const dealer = me?.dealers.find(
        (item) => item.id === route.params?.dealer_id,
      );
      setDealer((prevState) => dealer ?? prevState);
      setLicense((prevState) => route.params?.license ?? prevState);
      setDatetime((prevState) =>
        route.params?.datetime ? moment.utc(route.params?.datetime) : prevState,
      );
      setVINPart((prevState) => route.params?.vin_part ?? prevState);
      setMileage((prevState) => route.params?.kilometrage ?? prevState);
      setFirstName((prevState) => route.params?.first_name ?? prevState);
      setLastName((prevState) => route.params?.last_name ?? prevState);
      setEmail((prevState) => {
        const {value, hasError} = getRouteParamEmailState(
          route.params,
          prevState,
        );
        if (hasError) {
          setEmailError(true);
        }

        return value;
      });
      setPhoneNumber((prevState) => {
        const {value, hasError} = getRouteParamPhoneState(
          route.params,
          prevState,
        );
        if (hasError) {
          setPhoneError(true);
        }

        return value;
      });
      setServiceTypes((prevState) => route.params?.types ?? prevState);

      setInitialized(true);
    }, 100);
  }, [
    route.params,
    me,
    setDealer,
    setLicense,
    setDatetime,
    setVINPart,
    setMileage,
    setFirstName,
    setLastName,
    setEmail,
    setPhoneNumber,
    setServiceTypes,
    setInitialized,
    setEmailError,
    setPhoneError,
  ]);

  useEffect(() => {
    setExpectedCompletionDate(datetime);
  }, [datetime, setExpectedCompletionDate]);

  const reset = useCallback(() => {
    setFleetContract(null);
    setContract(null);
    setCar({});
    setVIN(null);
    setHasOpenStatus(false);
    setOverrideOpenStatus(false);
    setIsBusiness(null);
    setKvkName(null);
    setKvkNumber(null);
    setServiceTypes([]);
  }, [
    setFleetContract,
    setContract,
    setCar,
    setVIN,
    setHasOpenStatus,
    setOverrideOpenStatus,
    setIsBusiness,
    setKvkName,
    setKvkNumber,
    setServiceTypes,
  ]);
  const licenseCheck = useCallback(
    () =>
      runAppointmentLicenseCheck({
        license,
        dealer,
        overrideOpenStatus,
        reset,
        setHasOpenStatus,
        setFleetContract,
        setContract,
        setCar,
      }),
    [
      license,
      dealer,
      overrideOpenStatus,
      reset,
      setHasOpenStatus,
      setFleetContract,
      setContract,
      setCar,
    ],
  );

  useEffect(() => {
    if (!initialized) {
      return;
    }

    setServiceTypes([]);

    licenseCheck().then(({check, contract, hasOpenStatus}) => {
      setIsLicenseValid(!!check);

      setServiceTypes((prevState) => {
        if (shouldEnableRobServiceType({hasOpenStatus, isRetail, contract})) {
          return [...new Set([...prevState, SERVICE_TYPE_CODES.ROB])];
        }

        return prevState;
      });
    });
  }, [
    initialized,
    license,
    overrideOpenStatus,
    isRetail,
    licenseCheck,
    setIsLicenseValid,
    setServiceTypes,
  ]);
  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (
      shouldEnableInspectionByContract({
        contract,
        license,
        datetime,
        hasOpenStatus,
      })
    ) {
      setServiceTypes((prevState) => [
        ...new Set([
          ...(prevState ?? []),
          SERVICE_TYPE_CODES.Inspection,
          SERVICE_TYPE_CODES.ROB,
        ]),
      ]);
    }
  }, [
    initialized,
    license,
    datetime,
    contract,
    hasOpenStatus,
    setServiceTypes,
  ]);

  useEffect(() => {
    runAppointmentVinLookupEffect({
      initialized,
      license,
      vin_part,
      dealer,
      setRobNetError,
      setShowRobNetCredentialsDialog,
      setVIN,
      setVINPartIncorrect,
    }).catch(trace);
  }, [
    initialized,
    license,
    vin_part,
    dealer,
    setRobNetError,
    setShowRobNetCredentialsDialog,
    setVIN,
    setVINPartIncorrect,
  ]);
  useEffect(() => {
    if (!initialized) {
      return;
    }

    setStatus(resolveAppointmentStatusFromVin({datetime, vin}));
  }, [initialized, datetime, vin, setStatus]);
  useEffect(() => {
    if (isFinite(car?.last_kilometrage?.value) && isFinite(mileage)) {
      setShowKilometrageWarning(mileage <= car.last_kilometrage.value);
    }
  }, [mileage, car, setShowKilometrageWarning]);
  useEffect(() => {
    setReplacementType(REPLACEMENT_TYPE.none);

    setStatus(STATUS_CODES.Reserved);
  }, [dealer, setReplacementType, setStatus]);
  const isValid = computeAppointmentIsValid({
    serviceTypes,
    license,
    vin_part,
    car,
    vin_part_incorrect,
    contract,
    isRetail,
    isBusiness,
    kvk_number,
    kvk_name,
    email,
    phone_number,
    dealer,
    datetime,
  });

  const onRequest = async () => {
    await submitAppointmentRequest({
      isValid,
      licenseCheck,
      dispatch,
      status,
      license,
      mileage,
      initials,
      first_name,
      last_name,
      email,
      phone_number,
      dealer,
      datetime,
      fleetContract,
      contract,
      isBusiness,
      serviceTypes,
      vin_part,
      vin,
      replacementType,
      kvk_number,
      kvk_name,
      enableTecRMI,
      expected_completion_date,
      navigation,
    });
  };

  const renderVinPartWarningIcon = useCallback(
    ({size}) => (
      <Pressable onPress={() => setShowVinPartRequiredDialog(true)}>
        <ErrorOutlineIcon fill="#FF6C00" width={size} height={size} />
      </Pressable>
    ),
    [setShowVinPartRequiredDialog],
  );

  const renderRequestStatusIcon = useCallback(
    () => (
      <ServiceRequestStatusIcon
        status={status}
        fill="#FFFFFF"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    ),
    [status],
  );

  const contract_ends =
    contract?.leaseContract?.endDate &&
    moment(contract.leaseContract.endDate).isBefore(moment().add(3, 'months'));

  if (dealer && showVehicleSearch) {
    return (
      <VehicleSearch
        dealer_id={dealer.id}
        visible={true}
        onDismiss={() => setShowVehicleSearch(false)}
        onSelect={async (license) => {
          const car = await carApi.fetch(license);
          setCar(car);
          setLicense(license);
          setShowVehicleSearch(false);
        }}
      />
    );
  }

  return (
    <View style={sy.mainView}>
      <ScrollView
        style={[sy['bg-gray95']]}
        contentContainerStyle={[sy['gap-2'], sy['p-2']]}>
        <Grouping
          icon={
            <DealerPersona key={dealer?.id} dealer_id={dealer?.id} size={24} />
          }
          header={
            <Text style={[sy.mediumPlus, sy.truncate]}>
              {dealer?.city}&nbsp;&middot;&nbsp;{dealer?.short_name}
            </Text>
          }>
          <GroupingRow title="Vestiging" contentStyle={[sy['flex-1']]}>
            <Link
              textStyle={[sy['text-right'], sy['truncate']]}
              onPress={() => setChangeDealer(true)}>
              {dealer?.city}&nbsp;&middot;&nbsp;{dealer?.short_name}
            </Link>

            {changeDealer && (
              <ChangeDealerActionsheet
                dealer={dealer}
                onChangeDealer={(dealer) => {
                  setDealer(dealer);
                  setHasRetail(dealer?.has_retail);
                  setLicense('');
                  setVINPart(null);
                  setMileage(null);
                  setServiceTypes([]);
                  reset();
                }}
                onDismiss={() => setChangeDealer(false)}
              />
            )}
          </GroupingRow>
          <GroupingRow title="Label">
            <Link onPress={() => setChangeDealer(true)}>
              {dealer?.company_name}
            </Link>
          </GroupingRow>
        </Grouping>
        <Grouping
          icon={<EventIcon fill="#4A4A49" />}
          header={
            <>
              <Text style={[sy.mediumPlus, sy.capitalize]}>
                {moment(datetime, 'YYYY-MM-DD').format('dddd')}
              </Text>
              &nbsp;{moment(datetime, 'YYYY-MM-DD').format('D MMMM')}
              &nbsp;&middot;&nbsp;
              {moment.utc(datetime).format('H:mm')}
              &nbsp;uur
            </>
          }>
          <GroupingRow title="Datum">
            <Text>{moment(datetime, 'YYYY-MM-DD').format('D MMMM YYYY')}</Text>
            <GroupingRowEdit
              title="Datum wijzigen"
              dismissable
              cancelButton
              disableOKButton={!editDateTime}
              onClose={() => setEditDateTime(null)}
              onOK={() => {
                setDatetime((prevState) => {
                  editDateTime.hours(moment(prevState).utc().hours());
                  editDateTime.minutes(moment(prevState).minutes());

                  return editDateTime;
                });
              }}>
              <Calendar
                months={MONTHS_3_PAST_6_FUTURE}
                initialDate={today}
                date={editDateTime ?? datetime}
                onPress={setEditDateTime}
              />
            </GroupingRowEdit>
          </GroupingRow>
          <GroupingRow title="Tijd">
            <Text>{moment.utc(datetime).format('H:mm')}</Text>
            <GroupingRowEdit
              title="Tijd wijzigen"
              dismissable
              cancelButton
              disableOKButton={!editDateTime}
              onClose={() => setEditDateTime(null)}
              onOK={() => setDatetime(editDateTime)}>
              <ScrollView style={[sy['pl-4'], {maxHeight: 400}]}>
                <TimeSlots
                  begin_checkin={moment().startOf('day').hours(6)}
                  end_checkin={moment().startOf('day').hours(19)}
                  datetime={editDateTime ?? datetime}
                  onPress={setEditDateTime}
                />
              </ScrollView>
            </GroupingRowEdit>
          </GroupingRow>
          <GroupingRow
            title="Gereed (verwacht)"
            titleStyle={sy['whitespace-nowrap']}>
            <Text>
              {moment(expected_completion_date, 'YYYY-MM-DD').format(
                'D MMMM YYYY',
              )}
            </Text>
            <GroupingRowEdit title="Gereed (verwacht)" dismissable>
              <Calendar
                months={MONTHS_6_FUTURE}
                minDate={datetime}
                date={expected_completion_date ?? datetime}
                onPress={setExpectedCompletionDate}
              />
            </GroupingRowEdit>
          </GroupingRow>
        </Grouping>
        <Grouping
          icon={<DirectionsCarIcon fill="#4A4A49" />}
          header={car?.contract_name ?? car?.name ?? 'Voertuig'}
          initial={{expand: true}}>
          <View style={[sy['p-4'], sy['gap-4']]}>
            <View style={[sy['flex-row'], sy['gap-4']]}>
              <View style={[sy['py-4']]}>
                <PinIcon fill="#4a4a49" />
              </View>
              <View style={[sy['flex-1'], sy['gap-2']]}>
                <FormTextInput
                  value={license}
                  onChangeText={(text) => {
                    const license = text
                      ?.replace(/[^0-9a-z]/gi, '')
                      .toUpperCase();
                    setLicense(license);
                    return license;
                  }}
                  label="Kenteken"
                  required={true}
                  errorMessage={'Kenteken is verplicht'}
                  inputProps={{
                    maxLength: 8,
                    autoCapitalize: 'characters',
                  }}
                />
                {user_settings?.search_by_vehicle_number && (
                  <Link
                    style={[sy['px-3']]}
                    onPress={() => setShowVehicleSearch(true)}>
                    {contract?.vehicle?.reference ?? 'Voertuignummer'}
                  </Link>
                )}
              </View>
            </View>
            <View style={[sy['flex-row'], sy['gap-4']]}>
              <View style={[sy['py-4']]}>
                <FlagIcon fill="#4a4a49" />
              </View>
              <View style={[sy['flex-1'], sy['gap-2']]}>
                <FormTextInput
                  label="Meldcode"
                  value={vin_part}
                  errorMessage="Meldcode is onjuist"
                  hasError={vin_part_incorrect}
                  onChangeText={setVINPart}
                  inputProps={{
                    maxLength: 4,
                    keyboardType: 'numeric',
                    right: license.length > 0 && contract && !car.name && (
                      <TextInput.Icon
                        style={{backgroundColor: 'transparent'}}
                        icon={renderVinPartWarningIcon}
                      />
                    ),
                  }}
                />
                {vin && <Text style={{lineHeight: 20}}>{vin}</Text>}
              </View>
            </View>
            <View style={[sy['flex-row'], sy['gap-4']]}>
              <View style={[sy['py-4']]}>
                <SpeedIcon
                  fill={showKilometrageWarning ? '#FF6C00' : '#4a4a49'}
                />
              </View>
              <View style={[sy['flex-1']]}>
                <FormNumberInput
                  label="Kilometerstand"
                  value={mileage}
                  digits={0}
                  onChangeNumber={setMileage}
                  inputProps={{
                    activeUnderlineColor: showKilometrageWarning
                      ? '#FF6C00'
                      : null,
                  }}
                />
                {showKilometrageWarning && (
                  <Text style={{color: '#FF6C00', lineHeight: 20}}>
                    Laatste stand&nbsp;
                    {Format.number(car.last_kilometrage?.value, 0, true)} km (
                    {moment(car.last_kilometrage?.date).format('D-M-YYYY')})
                  </Text>
                )}
              </View>
            </View>
          </View>
        </Grouping>
        {initialized && (
          <>
            {isLicenseValid && isRetail && (
              <Grouping
                icon={
                  isBusiness === true ? (
                    <BusinessIcon fill="#4a4a49" />
                  ) : (
                    <IdCardIcon fill="#4a4a49" />
                  )
                }
                header={kvk_name ?? 'Eigendom'}
                initial={{expand: true}}>
                <Pressable
                  style={[sy['p-4'], sy['flex-row'], sy['gap-4']]}
                  onPress={() => {
                    setIsBusiness(false);
                    setKvkName(null);
                    setKvkNumber(null);
                  }}>
                  <RadioButton checked={isBusiness === false} />
                  <Text>Particulier</Text>
                </Pressable>
                <Divider style={[sy['ml-14'], sy['bg-gray95']]} />
                <Pressable
                  style={[sy['p-4'], sy['flex-row'], sy['gap-4']]}
                  onPress={() => {
                    setIsBusiness(true);
                    setKvkName(null);
                    setKvkNumber(null);
                  }}>
                  <RadioButton checked={isBusiness === true} />
                  <View>
                    <Text>Zakelijk</Text>
                    {isBusiness === true && (
                      <>
                        {kvk_number ? (
                          <Link
                            textStyle={[sy.small]}
                            onPress={() => setShowKvkDialog(true)}>
                            {kvk_name}
                          </Link>
                        ) : (
                          <Link
                            textStyle={[sy.small, {color: '#FF6C00'}]}
                            onPress={() => setShowKvkDialog(true)}>
                            Bedrijf zoeken (KVK)
                          </Link>
                        )}
                      </>
                    )}
                  </View>
                </Pressable>
              </Grouping>
            )}
            {renderAppointmentDetailsGate({
              hasRetail,
              car,
              contract,
              isLicenseValid,
              isRetail,
              isBusiness,
              kvk_number,
              children: (
                <>
                  <AppointmentLessorGrouping
                    isRetail={isRetail}
                    fleetContract={fleetContract}
                    contract={contract}
                    contract_ends={contract_ends}
                    car={car}
                    hasRetail={hasRetail}
                    isBusiness={isBusiness}
                    license={license}
                    dealer={dealer}
                    setContract={setContract}
                  />
                  <AppointmentPersonGrouping
                    initials={initials}
                    first_name={first_name}
                    last_name={last_name}
                    isValid={isValid}
                    emailError={emailError}
                    phoneError={phoneError}
                    isRetail={isRetail}
                    setInitials={setInitials}
                    setFirstName={setFirstName}
                    setLastName={setLastName}
                    phone_number={phone_number}
                    setPhoneNumber={setPhoneNumber}
                    setPhoneError={setPhoneError}
                    email={email}
                    setEmail={setEmail}
                    setEmailError={setEmailError}
                  />
                  <AppointmentAssignmentGrouping
                    isRetail={isRetail}
                    contract={contract}
                    hasOpenStatus={hasOpenStatus}
                    setShowDuplicateDialog={setShowDuplicateDialog}
                    serviceTypes={serviceTypes}
                    setServiceTypes={setServiceTypes}
                    isBusiness={isBusiness}
                    dispatch={dispatch}
                    setEnableTecRMI={setEnableTecRMI}
                    car={car}
                    mileage={mileage}
                    datetime={datetime}
                    setShowInspectionInfoDialog={setShowInspectionInfoDialog}
                    fleetContract={fleetContract}
                  />
                  <AppointmentExtraServiceGrouping
                    serviceTypes={serviceTypes}
                    hasOpenStatus={hasOpenStatus}
                    contract={contract}
                    replacementType={replacementType}
                    setReplacementType={setReplacementType}
                  />
                </>
              ),
            })}
          </>
        )}
      </ScrollView>
      <View style={[sy.footerView, sy['justify-end'], sy['items-center']]}>
        <PrimaryButton
          icon={renderRequestStatusIcon}
          disabled={!isValid}
          onPress={onRequest}>
          {requestButtonLabel}
        </PrimaryButton>
      </View>
      <DuplicateWarningDialog
        visible={showDuplicateDialog}
        onDismiss={() => setShowDuplicateDialog(false)}
        onOverride={() => setOverrideOpenStatus(true)}
      />
      <Dialog
        visible={showInspectionInfoDialog}
        title="APK bij onderhoud"
        titleIcon={<InfoIcon fill="#FF6C00" />}
        onDismiss={() => setShowInspectionInfoDialog(false)}>
        <View style={[sy['flex-row'], sy['py-4']]}>
          <Text>
            Verzoek LM: APK combineren met onderhoud bij vervaldatum binnen{' '}
            {moment(contract?.vehicle?.maintenance.nextPeriodicCheck).diff(
              moment(contract?.vehicle?.maintenance.nextPeriodicCheckExtended),
              'months',
            )}{' '}
            maanden.
          </Text>
        </View>
      </Dialog>
      <KvkSearchDialog
        visible={showKvkDialog}
        onDismiss={() => setShowKvkDialog(false)}
        onOK={(value) => {
          setKvkNumber(value.number);
          setKvkName(value.name);
          setShowKvkDialog(false);
        }}
      />
      <Dialog
        visible={showVinPartRequiredDialog}
        title="Meldcode opgeven"
        titleIcon={<ErrorOutlineIcon fill="#FF6C00" />}
        onDismiss={() => setShowVinPartRequiredDialog(false)}>
        <View style={[sy['flex-row'], sy['py-4']]}>
          <Text>
            Voor het ophalen van de voertuiggegevens is de meldcode vereist.
          </Text>
        </View>
      </Dialog>
      <RobNetCredentialsDialog
        visible={showRobNetCredentialsDialog}
        error={robNetError}
        onDismiss={() => setShowRobNetCredentialsDialog(false)}
        onOK={async (username, password) => {
          setShowRobNetCredentialsDialog(false);

          const {success, vin_number, rob_net_error} = await carApi.vinLookup(
            license,
            vin_part,
            dealer.id,
            {
              username,
              password,
            },
          );

          if (success) {
            setVIN(vin_number);
            setVINPartIncorrect(false);
          } else if (rob_net_error) {
            setVIN(null);
            setRobNetError(rob_net_error);
            setShowRobNetCredentialsDialog(true);
          } else {
            setVIN(null);
            setVINPartIncorrect(true);
          }
        }}
      />
    </View>
  );
};
Index.propTypes = {
  navigation: PropTypes.any,
  route: PropTypes.any,
};

export default withProtectedScreen(Index);
